import React, { FC, useMemo } from "react"
import { Navigate, useParams } from "react-router-dom"

import { Grid } from "@mantine/core"

import Page from "../components/layouts/page/Page"
import DeviceDetailsCard from "../features/devices/components/device-details-card/DeviceDetailsCard"
import DeviceEventsCard from "../features/devices/components/device-events-card/DeviceEventsCard"
import { FullscreenLoader } from "../components/fallbacks/FullscreenLoader"
import { HealthProvider } from "../contexts/health-context"
import DeviceTempChart from "../features/devices/components/device-temp-chart/DeviceTempChart"
import DeviceBatteryChart from "../features/devices/components/device-battery-chart/DeviceBatteryChart"
import DeviceStorageChart from "../features/devices/components/device-storage-chart/DeviceStorageChart"
import { ObservationsProvider } from "../contexts/observations-context"
// import DeviceObservationsCard from "../features/devices/components/device-observations-card/DeviceObservationsCard"
import { useDevices } from "../contexts/devices-context"
// import DeviceDistributionCard from "../features/devices/components/device-distribution-card/DeviceDistributionCard"
import DeviceBarChart from "../features/devices/components/device-bar-chart/DeviceBarChart"
import CameraModuleSettings from "../features/modules/components/camera-module-settings/CameraModuleSettings"
import AudioModuleSettings from "../features/modules/components/audio-module-settings/AudioModuleSettings"
import LightModuleSettings from "../features/modules/components/lights-module-settings/LightModuleSettings"
import OperatingTimeModuleSettings from "../features/modules/components/operating-time-module-settings/OperatingTimeModuleSettings"
import UploadModuleSettings from "../features/modules/components/upload-module-settings/UploadModuleSettings"
import PageDivider from "../components/layouts/page/PageDivider"
import { useLanguage } from "../contexts/language-context"
import DeviceSyncChart from "../features/devices/components/device-sync-chart/DeviceSyncChart"
import RequiresRole from "../features/auth/components/require-auth/RequiresRole"
import DevicePreviewCard from "../features/devices/components/device-preview-card/DevicePreviewCard"

const Device: FC = () => {
    const { id } = useParams()
    const { map, loading } = useDevices()
    const device = id ? map[id] : null
    const moduleNames = device?.modules.map(module => module.name)
    const { L } = useLanguage()

    return (
        <Page title={device?.name ?? ""} returnAction={L("Devices")}>
            {loading ? (
                <FullscreenLoader />
            ) : device ? (
                <HealthProvider device={device}>
                    <ObservationsProvider device={device}>
                        <Grid gutter="xl">
                            <Grid.Col span={12} lg={8}>
                                <DeviceDetailsCard device={device} />
                            </Grid.Col>
                            <Grid.Col span={12} lg={4}>
                                <DevicePreviewCard device={device} />
                            </Grid.Col>
                            <Grid.Col span={12}>
                                <PageDivider>{L("Observations")}</PageDivider>
                            </Grid.Col>
                            <Grid.Col span={12} lg={4} h={"40vh"}>
                                <DeviceBarChart />
                            </Grid.Col>
                            {/* Not currently tracking different species of insects */}
                            {/*<Grid.Col span={12} lg={4}>*/}
                            {/*    <DeviceDistributionCard />*/}
                            {/*</Grid.Col>*/}
                            {/*<Grid.Col span={12}>*/}
                            {/*    <DeviceObservationsCard />*/}
                            {/*</Grid.Col>*/}
                            {device.health?.battery !== null && (
                                <Grid.Col span={12} lg={4} h="40vh">
                                    <DeviceBatteryChart />
                                </Grid.Col>
                            )}
                            <Grid.Col span={12} lg={4} h="40vh">
                                <DeviceTempChart />
                            </Grid.Col>
                            <Grid.Col span={12} lg={4} h="40vh">
                                <DeviceStorageChart/>
                            </Grid.Col>
                            {device.health?.synced && (
                                <Grid.Col span={12} lg={4} h="40vh">
                                    <DeviceSyncChart/>
                                </Grid.Col>
                            )}
                            <RequiresRole role="edit-devices">
                                    <Grid.Col span={12}>
                                        <PageDivider>{L("Settings")}</PageDivider>
                                    </Grid.Col>
                                    {(moduleNames?.includes("lights") && device.type !== "Insectmoni_RPI4") && (
                                        <Grid.Col span={12} lg={6}>
                                            <LightModuleSettings.Device device={device} />
                                        </Grid.Col>
                                    )}
                                    {moduleNames?.includes("audio") && (
                                        <Grid.Col span={12} lg={6}>
                                            <AudioModuleSettings.Device device={device} />
                                        </Grid.Col>
                                    )}
                                    {moduleNames?.includes("camera") && device.type !== "Insectmoni_RPI4" && (
                                        <Grid.Col span={12} lg={6}>
                                            <CameraModuleSettings.Device device={device} />
                                        </Grid.Col>
                                    )}
                                    {moduleNames?.includes("wittypi") && (
                                        <Grid.Col span={12} lg={6}>
                                            <OperatingTimeModuleSettings.Device device={device} />
                                        </Grid.Col>
                                    )}
                                    {moduleNames?.includes("upload_client") && (
                                        <Grid.Col span={12} lg={6}>
                                            <UploadModuleSettings.Device device={device} />
                                        </Grid.Col>
                                    )}
                            </RequiresRole>
                            <Grid.Col span={12}>
                                <PageDivider>{L("Events")}</PageDivider>
                            </Grid.Col>
                            <Grid.Col span={12}>
                                <DeviceEventsCard />
                            </Grid.Col>
                        </Grid>
                    </ObservationsProvider>
                </HealthProvider>
            ) : (
                <Navigate to=".." relative="path" />
            )}
        </Page>
    )
}

export default Device
